/* ════════════════════════════════════════════════════════════════════════════════════════
   SKŁADANIE NAZWY I ŚCIEŻKI ZASOBU + TRZY WARIANTY LINKU — lustro backendowej
   `DogSoft.Application/Dyski/RegulaNazwyDysku.cs`.

   [polecenie właściciela 2026-09-12]
   „stwórz formularz do stworzenia nowego dysku, z tą samą mechaniką, określenie ścieżki
    KRO_S_FSP_PRO oznacza Projects, KRO_S_FSP_DEP oznacza departments, dyski są w ścieżce:
    \\129.1.160.74\projects i \\129.1.160.174\departments"
   „dodaj opcję kopiowania linka dla macbooka oraz dla windows, oraz co ważne, dodaj opcje
    kopiowania linka do sharepoint, jako oddzielne linki w sekcji konfiguracji tak by dało się
    to łatwo przeklikać (to samo w sekcji - moj-dzial)"

   ── DLACZEGO LUSTRO, A NIE WYŁĄCZNIE ENDPOINT ────────────────────────────────────────────
   Właściciel żąda podglądu NA ŻYWO: „Pokaż sklejoną nazwę i ścieżkę NA ŻYWO, zanim człowiek
   zapisze". Żądanie HTTP po każdym wpisanym znaku to 20 wywołań na jedno słowo (audyt wydajności
   2026-09-12: wejście na zakładkę HR = 60 żądań w sekundzie, 8× to samo). Składanie napisu jest
   czystą funkcją, więc liczymy je w przeglądarce; serwera pytamy WYŁĄCZNIE o to, czego przeglądarka
   wiedzieć nie może — czy nazwa jest już ZAJĘTA.

   ⚠ Obie strony mają ten sam zestaw zapadek (backend: `ImportIAutomapowanieDyskowTests`,
   front: `__tests__/dyskiImportAutomapowanie.test.js`) liczony na TYCH SAMYCH przykładach —
   rozjazd lustra z oryginałem zapala testy po obu stronach.
   ════════════════════════════════════════════════════════════════════════════════════════ */

/** Rodzaje zasobu plikowego. Wartości są KONTRAKTEM z backendem (parametr `rodzaj`). */
export const RODZAJ_UDZIALU = {
    projekty: "PRO",
    dzialy: "DEP",
};

export const PRZEDROSTKI = {
    [RODZAJ_UDZIALU.projekty]: "KRO_S_FSP_PRO_",
    [RODZAJ_UDZIALU.dzialy]: "KRO_S_FSP_DEP_",
};

export const SCIEZKI_UDZIALOW = {
    [RODZAJ_UDZIALU.projekty]: "\\\\129.1.160.74\\projects",
    [RODZAJ_UDZIALU.dzialy]: "\\\\129.1.160.174\\departments",
};

/** Etykiety dla człowieka — to, co widać na przełączniku w formularzu. */
export const ETYKIETY_UDZIALOW = {
    [RODZAJ_UDZIALU.projekty]: "Projects (zasoby projektowe)",
    [RODZAJ_UDZIALU.dzialy]: "Departments (zasoby działowe)",
};

/** Znaki, których nazwa grupy zabezpieczeń AD nieść NIE MOŻE. Lustro `RegulaNazwyDysku.ZnakiZakazane`. */
export const ZNAKI_ZAKAZANE = "\"/\\[]:;|=,+*?<>@";

/** Twardy limit `sAMAccountName` w Active Directory. */
export const MAKS_DLUGOSC_GRUPY = 64;

export const przedrostek = (rodzaj) => PRZEDROSTKI[rodzaj] || "";

export const sciezkaMatki = (rodzaj) => SCIEZKI_UDZIALOW[rodzaj] || "";

/** Pełna nazwa techniczna zasobu (bez wariantu uprawnień) — to jest `AssignableDrive.Name`. */
export const zlozNazwe = (rodzaj, nazwa) => przedrostek(rodzaj) + String(nazwa || "").trim();

/** Nazwa grupy AD dla wariantu uprawnień, np. `KRO_S_FSP_PRO_Foo_RW`. */
export const zlozNazweGrupy = (rodzaj, nazwa, uprawnienie) =>
    `${zlozNazwe(rodzaj, nazwa)}_${String(uprawnienie || "").trim().replace(/^_+/, "").toUpperCase()}`;

/** Obie grupy, które trzeba założyć w AD dla nowego zasobu — odczyt i zapis. */
export const grupyDoZalozenia = (rodzaj, nazwa) => {
    const n = String(nazwa || "").trim();
    if (!rodzaj || !n) return [];
    return [zlozNazweGrupy(rodzaj, n, "RW"), zlozNazweGrupy(rodzaj, n, "RO")];
};

/** Pełna ścieżka folderu: udział + nazwa. */
export const sciezkaPelna = (rodzaj, nazwa) => {
    const matka = sciezkaMatki(rodzaj);
    const n = String(nazwa || "").trim();
    if (!matka) return n;
    if (!n) return matka;
    return `${matka.replace(/\\+$/, "")}\\${n}`;
};

/* ── TRZY WARIANTY LINKU ─────────────────────────────────────────────────────────────────
   Windows i macOS to TEN SAM zasób zapisany dwiema składniami. Jeden przycisk „kopiuj ścieżkę"
   wymuszał na człowieku ręczne przerobienie `\\serwer\udzial` na `smb://serwer/udzial` — i tak
   powstawały zgłoszenia „nie mogę się podłączyć", bo ktoś wkleił backslashe do Findera. */

/** Link dla Windows: `\\serwer\udzial\nazwa` (Eksplorator / „Mapuj dysk sieciowy"). */
export const linkWindows = (matka, nazwa) => {
    const m = String(matka || "").trim().replace(/\\+$/, "");
    const n = String(nazwa || "").trim().replace(/^\\+|\\+$/g, "");
    if (!m) return n;
    return n ? `${m}\\${n}` : m;
};

/**
 * Link dla macOS: `smb://serwer/udzial/nazwa` (Finder → „Połącz z serwerem").
 *
 * 🔴 SPACJA W NAZWIE FOLDERU. Zastane zasoby nazywają się „ZZ_BIURO STRATEGII”. Finder przyjmie
 * surową spację, ale wklejenie takiego adresu do Teams albo do przeglądarki URYWA link na spacji.
 * Dlatego segmenty są kodowane procentowo, a backslashe zamienione na ukośniki — `smb://` to adres
 * URL, nie ścieżka UNC.
 */
export const linkMac = (matka, nazwa) => {
    const pelna = linkWindows(matka, nazwa);
    if (!pelna) return "";

    const segmenty = pelna
        .replace(/^\\+/, "")
        .split("\\")
        .filter(Boolean)
        .map((seg) => encodeURIComponent(seg));

    return segmenty.length ? `smb://${segmenty.join("/")}` : "";
};

/* ── NAZWA FOLDERU, NAZWA KANONICZNA, ŚCIEŻKI BEZ SUFIKSU (R18 / R21, 2026-09-13) ─────────
   Lustro `RegulaNazwyDysku.NazwaFolderu / NazwaKanoniczna / SciezkiDysku` (backend), liczone na TYCH
   SAMYCH przykładach po obu stronach (`NazwyKanoniczneISharePointSyncTests` ↔ `nazwyKanoniczneIsciezki.test.js`).

   Cytaty właściciela:
   • „usuń zewsząd nazwy _RW/_RO oraz zostaw te nazwy, a także dostaw kolejną nazwę w której będzie
      określona nazwa kanoniczna na przykładzie Analiza odpadu"
   • „popraw linki na zapis np. \\192.168.XXXX\PROJECT\Analiza_odchylen_KP (bez tego RW i RO) oraz co
      ważne smb://IP_nazwa/"
   • „RW RO oznacza grupę która nadaje uprawnienia z AD"

   🔴 Trzy różne napisy z jednej nazwy technicznej:
   • TECHNICZNA (`name`) — grupa AD, zostaje nietknięta (po niej IT nadaje dostęp);
   • FOLDERU (`nazwaFolderu`) — bez przedrostka i sufiksu, PODKREŚLENIA ZOSTAJĄ (tak nazywa się katalog);
   • KANONICZNA (`nazwaKanoniczna`) — dla człowieka, podkreślenia → spacje.
   Link zbudowany z nazwy grupy („…\Analiza_odchylen_KP_RW") prowadzi do katalogu, którego nie ma. */

/** Końcówka wariantu uprawnień — WYŁĄCZNIE za separatorem (`_RW`, `-RO`, `_R`, `_CO`, `_No_DEL`). */
const SUFIKS_UPRAWNIEN = /[_\- ](No_DEL|RW|RO|CO|R)$/i;

/** `Analiza_odpadu_RW` → `Analiza_odpadu`. `BHP_HR` zostaje `BHP_HR` (R to część słowa, nie sufiks). */
export const zdejmijSufiksUprawnien = (nazwa) => {
    const n = String(nazwa || "").trim();
    if (!n) return "";
    return n.replace(SUFIKS_UPRAWNIEN, "").trim();
};

/** Nazwa KATALOGU na serwerze: bez przedrostka i bez sufiksu, z zachowanymi podkreśleniami. */
export const nazwaFolderu = (nazwaTechniczna) => zdejmijSufiksUprawnien(odetnijPrzedrostek(nazwaTechniczna));

/** Nazwa KANONICZNA dla człowieka: `Analiza_odpadu_RW` → `Analiza odpadu`. */
export const nazwaKanoniczna = (nazwaTechniczna) => {
    const folder = nazwaFolderu(nazwaTechniczna);
    if (!folder) return "";
    return folder.replace(/_/g, " ").replace(/\s{2,}/g, " ").trim();
};

/* ── [zgłoszenie właściciela 2026-09-16] ADRES SERWERA PLIKÓW ────────────────────────────────
   Właściciel: „prosiłem by kopiowanie było zgodne z zapisem \\ścieżka\dysk\nazwa, a nie np.
   PROJECT\Badania_Profilaktyczne-Opisy_Stanowsk".

   POMIAR PRODUKCJI: wszystkie 394 zasoby mają w polu „dysk matka" samą nazwę UDZIAŁU — 333 razy
   `PROJECT` i 61 razy `Department` — bez serwera. Link z takiej wartości wychodził jako
   `PROJECT\Nazwa`, czego Eksplorator nie otworzy, bo nie wie, na czyim dysku szukać. To nie była
   usterka składania linku, tylko BRAKUJĄCY CZŁON ADRESU.

   Serwer plików jest JEDEN na firmę, więc jest USTAWIENIEM instalacji (AppSettings:
   `DyskiAdresSerwera`), a nie polem przy każdym z 394 zasobów. Trzymamy go w module, bo ten sam
   zestaw przycisków kopiowania renderują cztery różne drzewa komponentów (/admin/drives, okno
   zasobu, sekcja bez właściciela, /moj-dzial) — przeciąganie jednej wartości konfiguracyjnej przez
   wszystkie byłoby czterema łańcuchami propsów do tej samej stałej.

   Lustro backendu: `RegulaNazwyDysku.ZlozAdresUnc`. */
let adresSerweraPlikowWModule = "";

/** Zapamiętuje adres serwera plików dla ścieżek — woła go ekran, który wczytał ustawienie. */
export const ustawAdresSerweraPlikow = (adres) => {
    adresSerweraPlikowWModule = String(adres || "").trim().replace(/^[\\/]+/, "").replace(/[\\/]+$/, "");
};

/** Adres serwera plików albo pusty napis, gdy nie ustawiono. */
export const adresSerweraPlikow = () => adresSerweraPlikowWModule;

/**
 * Pełny adres UNC udziału: `\\serwer\udział`.
 *
 * ⚠️ BEZ USTAWIONEGO SERWERA ZWRACAMY PUSTKĘ, a nie połowę ścieżki. Połowiczna ścieżka wygląda jak
 * działająca i wraca zgłoszeniem „nie mogę się podłączyć" — dokładnie tym, które ta zmiana zamyka.
 * Zasób z własnym, pełnym adresem UNC zostaje nietknięty: ustawienie instalacji go NIE nadpisuje.
 */
export const zlozAdresUnc = (adresSerwera, masterDiskAddress) => {
    const matka = String(masterDiskAddress || "").trim().replace(/\\+$/, "");
    if (!matka) return "";
    if (matka.startsWith("\\\\")) return matka;

    // `??`, nie `||`: pusty napis podany JAWNIE znaczy „bez serwera", a nie „weź zapamiętany".
    const zrodlo = adresSerwera === undefined || adresSerwera === null ? adresSerweraPlikowWModule : adresSerwera;
    const serwer = String(zrodlo || "").trim()
        .replace(/^[\\/]+/, "")
        .replace(/[\\/]+$/, "");
    if (!serwer) return "";

    return `\\\\${serwer}\\${matka.replace(/^\\+/, "")}`;
};

/**
 * JEDNA czysta funkcja ścieżek zasobu — używana w /admin/drives, w /moj-dzial i w pickerze wniosku.
 * Windows `\\serwer\udział\nazwa_bez_sufiksu`, macOS `smb://serwer/udział/nazwa_bez_sufiksu`,
 * SharePoint — adres witryny. Brakujący wariant to pusty napis (przycisk wygaszony, nie kopiuje pustki).
 *
 * @param {object} dysk wiersz z listy zasobów (`AssignableDriveDto`)
 * @param {string} [adresSerwera] adres serwera plików; domyślnie ten zapamiętany w module
 * @returns {{windows: string, mac: string, sharepoint: string, folder: string}}
 */
export const sciezkiDysku = (dysk, adresSerwera) => {
    const witryna = dysk?.kind === "sharepoint";
    // [zgłoszenie właściciela 2026-09-16] PEŁNY adres UNC — z udziału zapisanego przy zasobie
    // i z adresu serwera plików (ustawienie instalacji). Patrz nota przy `zlozAdresUnc`.
    const matka = zlozAdresUnc(adresSerwera, dysk?.masterDiskAddress);
    // Nazwa widoczna bywa opisowa („Dysk projektowy działu IT”) — do ścieżki wchodzi nazwa TECHNICZNA,
    // odarta z przedrostka i z sufiksu grupy.
    const folder = nazwaFolderu(dysk?.name || "");

    if (witryna) return { windows: "", mac: "", sharepoint: String(dysk?.sharePointUrl || "").trim(), folder };
    if (!matka) return { windows: "", mac: "", sharepoint: "", folder };

    return {
        windows: linkWindows(matka, folder),
        mac: linkMac(matka, folder),
        sharepoint: "",
        folder,
    };
};

/** W nazwie nadanej przez człowieka zdejmujemy wyłącznie jawną końcówkę grupy RW/RO. */
const SUFIKS_GRUPY_W_NAZWIE_WIDOCZNEJ = /[_\- ](RW|RO)$/i;

/**
 * Nazwa kanoniczna ZASOBU z obu nazw — lustro `RegulaNazwyDysku.NazwaKanonicznaZasobu` (backend).
 * „zostaw te nazwy": nazwa WIDOCZNA nadana przez administratora (`friendlyName`, np. „Dysk projektowy
 * działu IT") wygrywa z regułą; reguła z grupy AD działa, gdy nazwy widocznej nie ma albo jest kopią
 * technicznej (ta sama, z podkreśleniami zamienionymi na spacje, albo wklejona grupa AD bez spacji).
 */
export const nazwaKanonicznaZasobu = (nazwaTechniczna, nazwaWidoczna) => {
    const techniczna = String(nazwaTechniczna || "").trim();
    const widoczna = String(nazwaWidoczna || "").trim();
    const rowne = (a, b) => a.toLowerCase() === b.toLowerCase();

    if (!widoczna
        || rowne(widoczna, techniczna)
        || rowne(widoczna, techniczna.replace(/_/g, " "))
        || rowne(widoczna, nazwaFolderu(techniczna))) {
        return nazwaKanoniczna(techniczna);
    }
    if (!/\s/.test(widoczna) && widoczna.includes("_")) return nazwaKanoniczna(widoczna);
    return widoczna.replace(SUFIKS_GRUPY_W_NAZWIE_WIDOCZNEJ, "").replace(/\s{2,}/g, " ").trim();
};

/**
 * Nazwa, którą człowiek ma zobaczyć — WSZĘDZIE (R18 „usuń zewsząd nazwy _RW/_RO"): /admin/drives, wniosek,
 * /moj-dzial, tytuły okien. Kolejność: kanoniczna z ewidencji → wyprowadzona z nazwy widocznej i technicznej
 * → techniczna. Lustro backendu: `RegulaNazwyDysku.EtykietaZasobu`.
 */
export const nazwaDlaCzlowieka = (dysk) =>
    String(dysk?.nazwaKanoniczna || "").trim()
    || nazwaKanonicznaZasobu(dysk?.name, dysk?.friendlyName)
    || String(dysk?.name || "").trim();

/**
 * Trzy linki JEDNEGO zasobu — dokładnie to, co mają kopiować trzy osobne przyciski.
 * Od R21 to ten sam wynik, co `sciezkiDysku` (nazwa bez sufiksu RW/RO); alias zostaje dla wołających.
 *
 * @param {object} dysk wiersz z listy zasobów (`AssignableDriveDto`)
 * @returns {{windows: string, mac: string, sharepoint: string}}
 */
export const linkiZasobu = (dysk) => sciezkiDysku(dysk);

/**
 * Zdejmuje przedrostek `KRO_S_FSP_PRO_` / `KRO_S_FSP_DEP_` z nazwy technicznej.
 *
 * ⚠ TO NIE JEST KOSMETYKA. Folder na serwerze nazywa się `Biuro_Strategii`, a grupa AD —
 * `KRO_S_FSP_PRO_Biuro_Strategii`. Link zbudowany z nazwy grupy prowadziłby do katalogu, którego
 * na dysku nie ma, i człowiek dostałby „nie znaleziono ścieżki sieciowej” bez cienia podpowiedzi.
 */
export const odetnijPrzedrostek = (nazwaTechniczna) => {
    const n = String(nazwaTechniczna || "").trim();
    for (const p of Object.values(PRZEDROSTKI)) {
        if (n.toUpperCase().startsWith(p.toUpperCase())) return n.slice(p.length);
    }
    return n;
};

/* ── WALIDACJA NAZWY ─────────────────────────────────────────────────────────────────────
   Lustro `RegulaNazwyDysku.WalidujNazwe`. Komunikaty są IDENTYCZNE po obu stronach: człowiek nie ma
   zobaczyć dwóch różnych zdań o tym samym błędzie, zależnie od tego, czy odezwała się przeglądarka,
   czy serwer. */

/**
 * @returns {string|null} komunikat dla człowieka albo null, gdy nazwa jest dobra
 */
export const walidujNazwe = (rodzaj, nazwa) => {
    const n = String(nazwa || "").trim();

    if (!n) return "Podaj nazwę zasobu — z niej powstaje nazwa grupy i folder na serwerze.";
    if (!rodzaj) return "Wybierz, gdzie ma powstać zasób: Projects czy departments.";

    if (n.includes(" ")) {
        return "Nazwa nowego zasobu nie może zawierać spacji — użyj podkreślenia, np. Biuro_Strategii.";
    }

    const zly = Array.from(n).find((c) => ZNAKI_ZAKAZANE.includes(c));
    if (zly) {
        return `Znak „${zly}” jest zakazany w nazwie grupy zabezpieczeń. Dozwolone są litery, cyfry, kropka, myślnik i podkreślenie.`;
    }

    if (n.startsWith("_") || n.endsWith("_") || n.startsWith(".") || n.endsWith(".")) {
        return "Nazwa nie może zaczynać się ani kończyć podkreśleniem albo kropką.";
    }

    const zPrzedrostkiem = Object.values(PRZEDROSTKI)
        .some((p) => n.toUpperCase().startsWith(p.toUpperCase()));
    if (zPrzedrostkiem) {
        return "Podaj samą nazwę zasobu — przedrostek KRO_S_FSP_… dokładany jest automatycznie.";
    }

    // Najdłuższy wariant grupy to nazwa + „_No_DEL". Sprawdzamy NAJGORSZY przypadek, bo grupa powstanie
    // w AD dopiero po zaakceptowaniu zgłoszenia — wtedy nikt już tego nie poprawi.
    const najdluzsza = zlozNazweGrupy(rodzaj, n, "No_DEL");
    if (najdluzsza.length > MAKS_DLUGOSC_GRUPY) {
        return `Nazwa jest za długa: pełna nazwa grupy ma ${najdluzsza.length} znaków, a Active Directory `
            + `przyjmuje najwyżej ${MAKS_DLUGOSC_GRUPY}. Skróć nazwę o ${najdluzsza.length - MAKS_DLUGOSC_GRUPY}.`;
    }

    return null;
};

/** Podpowiedź poprawnej nazwy (spacje → podkreślenia, znaki zakazane usunięte). NIE zmienia pola sama. */
export const zaproponujNazwe = (nazwa) => {
    const n = String(nazwa || "").trim();
    if (!n) return "";

    return Array.from(n)
        .map((c) => (c === " " ? "_" : c))
        .filter((c) => !ZNAKI_ZAKAZANE.includes(c))
        .join("")
        .replace(/_{2,}/g, "_")
        .replace(/^[_.]+|[_.]+$/g, "");
};

/**
 * Kopiowanie do schowka z UCZCIWYM wynikiem.
 *
 * 🔴 `navigator.clipboard` NIE ISTNIEJE w kontekście bez HTTPS ani w starszych przeglądarkach, a pod
 * Safari RZUCA, gdy wywołanie nie wynika wprost z kliknięcia. Bez zapasowej drogi przycisk „Kopiuj”
 * po prostu nic nie robił, a człowiek klikał go kilka razy, przekonany, że skopiował ścieżkę.
 * Dlatego: nowe API, a gdy odmówi — stare `execCommand` na ukrytym polu; zwracamy true/false,
 * żeby interfejs mógł napisać prawdę.
 */
export const skopiujDoSchowka = async (tekst) => {
    const t = String(tekst || "");
    if (!t) return false;

    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(t);
            return true;
        }
    } catch {
        /* spadamy do drogi zapasowej */
    }

    try {
        const pole = document.createElement("textarea");
        pole.value = t;
        pole.setAttribute("readonly", "true");
        pole.style.position = "fixed";
        pole.style.left = "-9999px";
        document.body.appendChild(pole);
        pole.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(pole);
        return Boolean(ok);
    } catch {
        return false;
    }
};
